/**
 * A ledger question as the owner reads and answers it.
 *
 * Kept out of `main.ts` for the reason `alert-rows.ts` is: the answering surface
 * is the one place in this client where a wrong pixel becomes a wrong decision
 * in a durable ledger, and it should be assertable on its own without a browser.
 *
 * Every control here is drawn from what the server said this question accepts.
 * Choices come from `actions`, the text box appears only when `allowsFreeform`
 * is true, and a question that is not `pending` gets no controls at all — the
 * page says what state it reached instead. Nothing on this surface submits an
 * answer the person did not enter.
 */

import type { AnswerDraft } from "./request-answer.js";
import { allowsFreeform, answerability, chosenLabel, choicesOf, stateLabel } from "./request-answer.js";
import type { LedgerNotification, LedgerRequest } from "./platform-client.js";
import {
  HISTORY_TRUNCATED,
  NOTHING_OUTSTANDING,
  moreToRead,
  outstandingSummary,
  type OutstandingCounts,
} from "./question-inbox.js";
import { escapeHtml } from "./vault.js";

/** Where the question's subject opens, resolved by the caller that knows the vault. */
export interface SubjectLink {
  readonly kind: "destination" | "external" | "plain";
  /** A destination path for `data-go`, an absolute https url, or the raw ref. */
  readonly value: string;
}

/** One earlier message in the same thread, from either history. */
export type ThreadEntry =
  | { readonly kind: "request"; readonly record: LedgerRequest }
  | { readonly kind: "notification"; readonly record: LedgerNotification };

export interface RequestDetailState {
  readonly request: LedgerRequest;
  readonly subject: SubjectLink;
  readonly draft: AnswerDraft;
  /** True while an answer is in flight, so the controls cannot be double-sent. */
  readonly submitting: boolean;
  /** Why the last submission failed, shown verbatim next to a retry. */
  readonly error: string | null;
  readonly thread: readonly ThreadEntry[];
  /**
   * True when the thread read stopped at a cursor rather than the end.
   *
   * Rendered as a sentence rather than swallowed: a history that looks complete
   * and is not would let a reader conclude something never happened.
   */
  readonly threadTruncated: boolean;
}

const PHASE: Readonly<Record<string, string>> = {
  pending: "waiting",
  answered: "active",
  consumed: "settled",
  expired: "settled",
  withdrawn: "settled",
  superseded: "settled",
};

function phasePill(request: LedgerRequest): string {
  const phase = PHASE[request.state] ?? "settled";
  return `<span class="phase-pill ${phase}">${escapeHtml(stateLabel(request.state))}</span>`;
}

/** The headline: the question's own title when it has one, its reason otherwise. */
export function headline(request: LedgerRequest): string {
  const title = request.title?.trim();
  return title !== undefined && title.length > 0 ? title : request.reason;
}

function subjectControl(subject: SubjectLink, label = "Open what this question is about"): string {
  switch (subject.kind) {
    case "destination":
      return `<button class="quiet-button" type="button" data-go="${escapeHtml(subject.value)}">${label}</button>`;
    case "external":
      return `<a class="quiet-button" href="${escapeHtml(subject.value)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    case "plain":
      return `<p class="reader-meta">About ${escapeHtml(subject.value)}</p>`;
  }
}

/**
 * The question's structured context, when it carried any.
 *
 * Rendered as plain labelled lines rather than raw JSON: the owner is being
 * asked to decide something, and the numbers a job attached are part of the
 * question rather than debugging output.
 */
function contextRows(context: Record<string, unknown> | null | undefined): string {
  if (context == null || Object.keys(context).length === 0) return "";
  const rows = Object.entries(context).map(([key, value]) => {
    const rendered = typeof value === "string" ? value : JSON.stringify(value);
    return `<div class="request-context-row"><dt>${escapeHtml(key)}</dt><dd>${escapeHtml(rendered ?? "")}</dd></div>`;
  }).join("");
  return `<dl class="request-context">${rows}</dl>`;
}

/**
 * The controls, drawn only for a question that is genuinely open.
 *
 * A closed question renders its state sentence instead. That asymmetry is the
 * point: there is no disabled-but-present Submit that a person can click and be
 * told "no" by, and no control at all on a question whose answer would be a
 * fiction.
 */
function answerForm(state: RequestDetailState): string {
  const { request, draft, submitting, error } = state;
  const open = answerability(request);
  if (!open.answerable) {
    return `<p class="request-closed" role="status">${escapeHtml(open.detail)}</p>`;
  }
  const choices = choicesOf(request);
  const selected = (draft.choiceId ?? "").trim();
  const buttons = choices.map((choice) => {
    const chosen = choice.id === selected;
    return `<button class="choice-button${choice.destructive ? " destructive" : ""}" type="button"`
      + ` data-answer-choice="${escapeHtml(choice.id)}"`
      + ` aria-pressed="${chosen ? "true" : "false"}"${submitting ? " disabled" : ""}>`
      + `${escapeHtml(choice.label)}</button>`;
  }).join("");
  const choiceGroup = choices.length === 0
    ? ""
    : `<div class="choice-row" role="group" aria-label="Answers this question offers">${buttons}</div>`;
  const freeform = allowsFreeform(request)
    ? `<label class="answer-label" for="answer-text">${choices.length === 0 ? "Your answer" : "Add a reply (optional)"}</label>`
      + `<textarea class="answer-text" id="answer-text" data-answer-text rows="3"`
      + ` placeholder="Type your answer"${submitting ? " disabled" : ""}>${escapeHtml(draft.text ?? "")}</textarea>`
    : `<p class="reader-meta">This question takes one of the answers above.</p>`;
  const failure = error === null
    ? ""
    : `<p class="answer-error" role="alert">${escapeHtml(error)}</p>`;
  return `<form class="answer-form" data-answer-form aria-label="Answer this question">`
    + choiceGroup
    + freeform
    + failure
    + `<div class="answer-actions">`
    + `<button class="quiet-button" type="submit" data-answer-submit${submitting ? " disabled" : ""}>`
    + `${error === null ? "Submit answer" : "Try again"}</button>`
    + (submitting ? `<span class="reader-meta" role="status">Sending your answer…</span>` : "")
    + `</div></form>`;
}

/** The decision the ledger holds, said in the words it was made in. */
function decision(request: LedgerRequest): string {
  const label = chosenLabel(request);
  const rows = [
    label === null ? "" : `<p>Chose: <strong>${escapeHtml(label)}</strong></p>`,
    request.answer === null || request.answer === label ? "" : `<p>Answer: ${escapeHtml(request.answer)}</p>`,
    request.outcome == null ? "" : `<p>Outcome: ${escapeHtml(request.outcome)}</p>`,
    request.retiredReason == null ? "" : `<p>Reason: ${escapeHtml(request.retiredReason)}</p>`,
  ].join("");
  return rows === "" ? "" : `<div class="domain-empty">${rows}</div>`;
}

/** Links to the version this replaced and the version that replaced it. */
function replacement(request: LedgerRequest): string {
  const parts: string[] = [];
  if (request.supersededBy != null) {
    parts.push(`<button class="quiet-button" type="button" data-go="ledger-request/${escapeHtml(request.supersededBy)}">`
      + `Open the question that replaced this</button>`);
  }
  if (request.supersedesId != null) {
    parts.push(`<button class="quiet-button" type="button" data-go="ledger-request/${escapeHtml(request.supersedesId)}">`
      + `Open the question this replaced</button>`);
  }
  if (request.threadParent != null) {
    const kind = request.threadParent.kind;
    parts.push(kind === "request"
      ? `<button class="quiet-button" type="button" data-go="ledger-request/${escapeHtml(request.threadParent.id)}">`
        + `Open what this follows up on</button>`
      : `<p class="reader-meta">Follows a notice in this thread.</p>`);
  }
  return parts.join("");
}

function threadRow(entry: ThreadEntry): string {
  if (entry.kind === "notification") {
    const notice = entry.record;
    return `<article class="domain-row">`
      + `<strong>${escapeHtml(notice.title)}</strong>`
      + `<small>Notice · ${escapeHtml(notice.state === "published" ? "Published" : notice.state === "superseded" ? "Replaced" : "Withdrawn")}</small>`
      + `<p class="reader-meta">${escapeHtml(notice.body)}</p>`
      + `<time>${escapeHtml(new Date(notice.createdAt).toLocaleString())}</time>`
      + `</article>`;
  }
  const past = entry.record;
  return `<article class="domain-row">`
    + `<button class="domain-open" type="button" data-go="ledger-request/${escapeHtml(past.requestId)}">`
    + `<strong>${escapeHtml(headline(past))}</strong>`
    + `<small>Question · ${escapeHtml(stateLabel(past.state))}</small>`
    + `<time>${escapeHtml(new Date(past.createdAt).toLocaleString())}</time>`
    + `</button>`
    + (past.answer == null ? "" : `<p class="reader-meta">Answer: ${escapeHtml(past.answer)}</p>`)
    + `</article>`;
}

function threadSection(state: RequestDetailState): string {
  const earlier = state.thread.filter((entry) =>
    entry.kind === "notification" || entry.record.requestId !== state.request.requestId);
  if (earlier.length === 0 && !state.threadTruncated) return "";
  const rows = [...earlier]
    .sort((left, right) => right.record.createdAt.localeCompare(left.record.createdAt))
    .map(threadRow).join("");
  const truncated = state.threadTruncated
    ? `<p class="reader-meta truncated">${escapeHtml(HISTORY_TRUNCATED)}</p>`
    : "";
  return `<section class="request-thread" aria-label="Earlier in this thread">`
    + `<h2 class="section-label">Earlier in this thread</h2>${truncated}${rows}</section>`;
}

/** The whole detail body for one ledger question. */
export function ledgerRequestView(state: RequestDetailState): string {
  const { request } = state;
  const body = request.body.trim();
  return `<div class="domain-state">${phasePill(request)}`
    + `<p>${escapeHtml(request.reason)}</p></div>`
    + `<div class="domain-empty"><strong>${escapeHtml(headline(request))}</strong>`
    + (body.length === 0 ? "" : `<p>${escapeHtml(body)}</p>`)
    + `</div>`
    + contextRows(request.context)
    + answerForm(state)
    + decision(request)
    + subjectControl(state.subject)
    + replacement(request)
    + threadSection(state);
}

/** The meta line above the body: who is being asked, and which record this is. */
export function ledgerRequestMeta(request: LedgerRequest): string {
  return `${request.direction === "to_owner" ? "Asked of you" : "Asked of an agent"} · ${request.requestId}`;
}

const NOTICE_PHASE: Readonly<Record<string, string>> = {
  published: "settled",
  superseded: "settled",
  withdrawn: "settled",
};

/** What state a notice reached, in the words the reader sees. */
export function noticeStateLabel(state: string): string {
  return state === "published" ? "Published" : state === "superseded" ? "Replaced" : "Withdrawn";
}

/**
 * The whole detail body for one notice.
 *
 * A notice is the half of the ledger that asks nothing: it carries its own
 * content, takes no answer, and is finished with by reading it. So this view is
 * deliberately the answering surface with the answering removed — same headline,
 * body, context and subject link, and a sentence saying outright that nothing
 * here needs a reply, so no control can be mistaken for one.
 */
export function noticeView(notice: LedgerNotification, subject: SubjectLink): string {
  const body = notice.body.trim();
  const phase = NOTICE_PHASE[notice.state] ?? "settled";
  return `<div class="domain-state">`
    + `<span class="phase-pill ${phase}">${escapeHtml(noticeStateLabel(notice.state))}</span>`
    + `<p>${escapeHtml(notice.reason)}</p></div>`
    + `<div class="domain-empty"><strong>${escapeHtml(notice.title)}</strong>`
    + (body.length === 0 ? "" : `<p>${escapeHtml(body)}</p>`)
    + `</div>`
    + contextRows(notice.context)
    + `<p class="request-closed" role="status">This is a notice. Nothing here needs an answer.</p>`
    + subjectControl(subject, "Open what this notice is about")
    + (notice.supersededBy == null
      ? ""
      : `<button class="quiet-button" type="button" data-go="notice/${escapeHtml(notice.supersededBy)}">`
        + `Open the notice that replaced this</button>`)
    + (notice.supersedesId == null
      ? ""
      : `<button class="quiet-button" type="button" data-go="notice/${escapeHtml(notice.supersedesId)}">`
        + `Open the notice this replaced</button>`)
    + (notice.threadParent == null
      ? ""
      : notice.threadParent.kind === "request"
        ? `<button class="quiet-button" type="button" data-go="ledger-request/${escapeHtml(notice.threadParent.id)}">`
          + `Open what this follows up on</button>`
        : `<button class="quiet-button" type="button" data-go="notice/${escapeHtml(notice.threadParent.id)}">`
          + `Open what this follows up on</button>`);
}

/** The meta line above a notice: what it is, and which record this is. */
export function noticeMeta(notice: LedgerNotification): string {
  return `Told to you · ${notice.notificationId}`;
}

/**
 * When a question was asked.
 *
 * Deliberately just the stamp: the Apple client's `LedgerRequest` carries no
 * deadline field, and a row that says more on one client than the other is the
 * drift the two-client rule exists to prevent.
 */
function askedLine(request: LedgerRequest): string {
  return `Asked ${new Date(request.createdAt).toLocaleString()}`;
}

/** One row of the outstanding-questions inbox. */
function inboxRow(request: LedgerRequest): string {
  return `<article class="domain-row">`
    + `<button class="domain-open" type="button" data-go="ledger-request/${escapeHtml(request.requestId)}">`
    + `<strong>${escapeHtml(headline(request))}</strong>`
    + `<small>${escapeHtml(stateLabel(request.state))} · ${escapeHtml(request.reason)}</small>`
    + `<time>${escapeHtml(askedLine(request))}</time>`
    + `</button></article>`;
}

/**
 * The outstanding-questions inbox: what the owner still owes an answer.
 *
 * The summary reports the server's tenant-wide counts rather than the length of
 * this list, and a listing with more pages behind it says so and offers to read
 * them — it never renders a first page as if it were the whole queue.
 */
export function questionInboxView(state: {
  readonly requests: readonly LedgerRequest[];
  readonly counts: OutstandingCounts;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
}): string {
  const summary = `<div class="domain-state"><p>${escapeHtml(outstandingSummary(state.counts))}</p></div>`;
  if (state.requests.length === 0) {
    return summary + `<div class="domain-empty"><strong>Nothing needs you</strong>`
      + `<p>${escapeHtml(NOTHING_OUTSTANDING)}</p></div>`;
  }
  const rows = state.requests.map(inboxRow).join("");
  const more = state.hasMore
    ? `<p class="reader-meta truncated">${escapeHtml(moreToRead(true))}</p>`
      + `<button class="quiet-button" type="button" data-questions-more`
      + `${state.loadingMore ? " disabled" : ""}>`
      + `${state.loadingMore ? "Reading…" : "Read more questions"}</button>`
    : "";
  return summary + rows + more;
}
